import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BlogPostShell, POSTS } from "@/pages/Blog";

const POST = POSTS.find((p) => p.slug === "troubleshooting-and-best-practices");
const OG = "https://cameraclubscore.com/og/blog-generic.png";
const URL = "https://cameraclubscore.com/blog/troubleshooting-and-best-practices";

export default function PostTroubleshoot() {
  return (
    <>
      <Helmet>
        <title>{`${POST.title} — Camera Club Score`}</title>
        <meta name="description" content={POST.excerpt} />
        <link rel="canonical" href={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={URL} />
        <meta property="og:title" content={POST.title} />
        <meta property="og:description" content={POST.excerpt} />
        <meta property="og:image" content={OG} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={OG} />
      </Helmet>
      <BlogPostShell post={POST}>
        <p className="lead">
          Ten years of watching camera clubs run competitions has produced
          a short list of practices that prevent 90% of judging-night
          drama, season-end panic, and committee-handover chaos. Steal any
          of them.
        </p>

        <h2>The ten practices that prevent 90% of problems</h2>

        <h3>1. Lock the running order before judging starts</h3>
        <p>
          Nothing derails a session faster than the grid re-sorting
          mid-competition. Camera Club Score's <strong>Lock order</strong>
          {" "}toggle freezes the sequence so it can't shuffle.
        </p>

        <h3>2. Never share a single admin login</h3>
        <p>
          When the current admin retires, they take the login knowledge
          with them. Every member of the committee who touches the tool
          should have their own login. It's free.
        </p>

        <h3>3. Auto-save everything</h3>
        <p>
          Manual "save" buttons are landmines. Camera Club Score saves on
          every keystroke — even if the wifi drops, changes are queued
          locally and flushed when connectivity returns.
        </p>

        <h3>4. Publish a public portal</h3>
        <p>
          A read-only public link lets members check their standings from
          their phone during the week. Fewer emails to the Secretary, more
          engagement. Free with Camera Club Score, no login needed.
        </p>

        <h3>5. Back up before committee handovers</h3>
        <p>
          At every AGM, export the year's data. Camera Club Score's
          Backup/Restore under Settings produces a portable JSON — put it
          in the club's shared drive.
        </p>

        <h3>6. Anonymise the running order</h3>
        <p>
          Judges shouldn't see who took the photo during scoring. Codes
          only. Names appear on the results page — never before.
        </p>

        <h3>7. Set themes at the start of the season</h3>
        <p>
          Themes announced eight-plus weeks ahead produce genuinely
          thought-out photography. Themes announced next week produce
          back-catalogue scrolling.
        </p>

        <h3>8. Rotate visiting judges</h3>
        <p>
          Three to four different judges per season prevents any one
          judge's aesthetic from dominating the league table.
        </p>

        <h3>9. Keep "Not counted" competitions</h3>
        <p>
          Practice rounds, inter-club battles, exhibition-only comps —
          flag them as <strong>Not counted</strong> so they don't skew the
          season league. Camera Club Score handles this per-competition.
        </p>

        <h3>10. Actually use a purpose-built tool</h3>
        <p>
          Spreadsheets served camera clubs beautifully for two decades.
          But if the sentence "we'll have to ask Barbara" comes up in
          your handovers, it's time.
        </p>

        <h2>Common problems and how to solve them</h2>

        <h3>"My scores didn't save on judging night"</h3>
        <p>
          Almost always caused by relying on a manual save button. Camera
          Club Score auto-saves — but if you're on a different tool, the
          fix is to enable auto-save (or migrate).
        </p>

        <h3>"Two photographers are tied for first"</h3>
        <p>
          Not a problem — declare joint first. Camera Club Score marks
          both with a "Tied" chip so nobody's surprised.
        </p>

        <h3>"The judge scored someone twice by mistake"</h3>
        <p>
          Every score in Camera Club Score is editable until the
          competition is locked. Correct the duplicate, save, done.
        </p>

        <h3>"We lost a print"</h3>
        <p>
          Sign-in / sign-out sheet at the door. See the pillar on
          <Link to="/blog/print-competitions-without-losing-prints">
            {" "}running print competitions without losing prints
          </Link>.
        </p>

        <p>
          None of this is complicated. It just isn't taught. Now you know.
        </p>

        <p><em>— Mick, founder of Camera Club Score</em></p>
      </BlogPostShell>
    </>
  );
}
